import { observer } from "mobx-react-lite"
import styles from "./styles.module.scss"
import CloseModalButton from "../../../../buttons/CloseModalButton"
import { makeAutoObservable } from "mobx"
import { match } from "ts-pattern"
import { MyProfileTab } from "./tabs/MyProfileTab"
import { useMemo } from "react"
import { CruiseLinesTab } from "./tabs/CruiseLinesTab"
import { BillingTab } from "./tabs/BillingTab"
import { TermsAndConditionsTab } from "./tabs/TermsAndConditionsTab"
import { SidebarButton } from "./components/SidebarButton"
import { SocialSellingTab } from "./tabs/SocialSellingTab"
import { useStores } from "../../../../../models/helpers/useStores"
import { ConfirmationModal } from "../../../../modals/ConfirmationModal"
import { useMutation } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { signOut } from "../../../../../services/auth"
import classNames from "classnames"

type AccountModalTab =
  | "myProfile"
  | "cruiseLines"
  | "billing"
  | "termsAndConditions"
  | "socialSelling"
  | "integrations"

class AccountModalStore {
  activeTab: AccountModalTab = "myProfile"
  incomingTab: AccountModalTab | null = null
  isModalWarningVisible = false
  isContentChanged = false

  constructor() {
    makeAutoObservable(this)
  }

  setActiveTab(tab: AccountModalTab) {
    this.activeTab = tab
  }

  setIsModalWarningVisible(visible: boolean) {
    this.isModalWarningVisible = visible
  }

  setIsContentChanged(changed: boolean) {
    this.isContentChanged = changed
  }

  setIncomingTab(tab: AccountModalTab) {
    this.incomingTab = tab
  }
}

type AvailableIcons = "person" | "fileText" | "cruiseShip" | "pricetags" | "creditCard" | "link"

type SidebarButton = {
  iconName: AvailableIcons
  label: string
  id: AccountModalTab
  isCruisenDeveloperOnly?: boolean
}

const sidebarButtons: SidebarButton[] = [
  {
    iconName: "person",
    label: "My profile",
    id: "myProfile",
  },
  {
    iconName: "fileText",
    label: "Terms & conditions",
    id: "termsAndConditions",
  },
  {
    iconName: "cruiseShip",
    label: "Cruise lines",
    id: "cruiseLines",
  },
  {
    iconName: "pricetags",
    label: "Social selling",
    id: "socialSelling",
    isCruisenDeveloperOnly: true,
  },
  {
    iconName: "creditCard",
    label: "Billing",
    id: "billing",
  },
  {
    iconName: "link",
    label: "Integrations",
    id: "integrations",
    isCruisenDeveloperOnly: true,
  },
]

export const AccountModal = observer(function AccountModal({
  closeModal,
  openSubscriptionModal,
}: {
  closeModal: () => void
  openSubscriptionModal: () => void
}) {
  const rootStore = useStores()
  const { loggedInUser } = rootStore
  const store = useMemo(() => new AccountModalStore(), [])
  const currentLabel = sidebarButtons.find((button) => button.id === store.activeTab)?.label

  const navigate = useNavigate()

  const { mutate: mutateLogout, isPending: isLoggingOut } = useMutation({
    mutationFn: () => signOut(),
    onSuccess: () => {
      navigate("/")
    },
  })

  return (
    <div className={styles.accountModal} data-testid="account-modal">
      <div className={styles.sidebar}>
        <p className={styles.sidebarTitle}>Settings</p>
        <div className={styles.sidebarButtonsContainer}>
          {sidebarButtons.map((button) => {
            if (button?.isCruisenDeveloperOnly && !rootStore.isCruisenDeveloper) {
              return null
            }

            return (
              <SidebarButton
                key={button.id}
                iconName={button.iconName}
                label={button.label}
                isActive={store.activeTab === button.id}
                onClick={() => {
                  if (store.activeTab === button.id) return

                  if (store.isContentChanged) {
                    store.setIncomingTab(button.id)
                    store.setIsModalWarningVisible(true)

                    return
                  }

                  store.setActiveTab(button.id)
                }}
              />
            )
          })}
        </div>
      </div>
      <div className={styles.accountModalWrapper}>
        <div className={styles.accountModalHeader}>
          <h2>{currentLabel}</h2>
          <CloseModalButton
            onClick={() => {
              store.setIncomingTab(null)
              if (store.isContentChanged) {
                store.setIsModalWarningVisible(true)
              } else {
                closeModal()
              }
            }}
            testId="close-account-modal-button"
            disabled={isLoggingOut}
          />
        </div>
        {match(store.activeTab)
          .with("myProfile", () => (
            <MyProfileTab
              openSubscriptionModal={openSubscriptionModal}
              closeModal={closeModal}
              setIsModalWarningVisible={(value) => store.setIsModalWarningVisible(value)}
              setIsContentChanged={(value) => store.setIsContentChanged(value)}
              logout={mutateLogout}
              isLoggingOut={isLoggingOut}
            />
          ))
          .with("cruiseLines", () => (
            <CruiseLinesTab
              closeModal={closeModal}
              setIsModalWarningVisible={(value) => store.setIsModalWarningVisible(value)}
              setIsContentChanged={(value) => store.setIsContentChanged(value)}
            />
          ))
          .with("billing", () => (
            <div className={styles.accountModalBody}>
              <BillingTab />
            </div>
          ))
          .with("termsAndConditions", () => (
            <TermsAndConditionsTab
              closeModal={closeModal}
              setIsModalWarningVisible={(value) => store.setIsModalWarningVisible(value)}
              setIsContentChanged={(value) => store.setIsContentChanged(value)}
            />
          ))
          .with("socialSelling", () => (
            <div className={styles.accountModalBody}>
              <SocialSellingTab />
            </div>
          ))
          .with("integrations", () => (
            <div className={classNames(styles.accountModalBody, styles.integrations)}>
              <zapier-workflow
                sign-up-email={loggedInUser?.email}
                sign-up-first-name={loggedInUser?.firstName}
                sign-up-last-name={loggedInUser?.lastName}
                client-id="Ey3wAEV82sNPbal6EW2A4wqCF2TyHmcqtWJG3i82"
                theme="dark"
                intro-copy-display="show"
                guess-zap-display="hide"
                template-ids="255643695,255643697,255643698"
              />
            </div>
          ))
          .exhaustive()}
      </div>
      {store.isModalWarningVisible && (
        <ConfirmationModal
          title="Unsaved changes"
          description="Are you sure you want to discard all unsaved changes?"
          onConfirm={() => {
            if (store.incomingTab) {
              store.setActiveTab(store.incomingTab)

              store.setIsModalWarningVisible(false)
              store.setIsContentChanged(false)
              store.setIncomingTab(null)
            } else {
              closeModal()
            }
          }}
          closeModal={() => store.setIsModalWarningVisible(false)}
          confirmButtonText="Discard"
          isLoading={false}
          testId="discard-unsaved-changes-modal"
          isPrimary={false}
        />
      )}
    </div>
  )
})
